import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../theme/ThemeContext';
import { StaffFormRow, StaffCredentialsModal } from '../components/staff';
import { ConfirmationModal } from '../components/common';
import useDepartmentStore from '../stores/departmentStore';
import { createEmptyStaffRow } from '../constants/staff';
import { validateStaffList } from '../utils/staffValidation';
import { bulkCreateStaff } from '../services/api/staffApi';
import { ROUTES } from '../constants/routes';
import './StaffOnboardingPage.css';

/**
 * StaffOnboardingPage
 *
 * Reached after organisation registration confirmation.
 * Allows adding multiple staff members via repeatable form rows
 * and submitting all in a single bulk API call.
 */
function StaffOnboardingPage() {
  const navigate = useNavigate();
  const { theme } = useTheme();

  const [staffRows, setStaffRows] = useState([createEmptyStaffRow()]);
  const [rowErrors, setRowErrors] = useState([{}]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [serverError, setServerError] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [createdCredentials, setCreatedCredentials] = useState([]);

  // ── Optional Department picker (single source of truth: departmentStore) ──
  const departments = useDepartmentStore((s) => s.departments);
  const deptsLoaded = useDepartmentStore((s) => s.deptsLoaded);
  const fetchDepartments = useDepartmentStore((s) => s.fetchDepartments);

  // Fetch the dept list ONCE. Guarded by deptsLoaded (store dedups) with stable
  // primitive deps — no request storm.
  useEffect(() => {
    if (!deptsLoaded) fetchDepartments();
  }, [deptsLoaded, fetchDepartments]);

  // ── Row manipulation ──────────────────────────────────────────────

  const addRow = useCallback(() => {
    setStaffRows((prev) => [...prev, createEmptyStaffRow()]);
    setRowErrors((prev) => [...prev, {}]);
  }, []);

  const removeRow = useCallback(
    (index) => {
      if (staffRows.length <= 1) return; // keep at least one row
      setStaffRows((prev) => prev.filter((_, i) => i !== index));
      setRowErrors((prev) => prev.filter((_, i) => i !== index));
    },
    [staffRows.length]
  );

  const handleFieldChange = useCallback((index, field, value) => {
    setStaffRows((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
    // Clear field-level error on change
    setRowErrors((prev) => {
      const updated = [...prev];
      if (updated[index]?.[field]) {
        updated[index] = { ...updated[index] };
        delete updated[index][field];
      }
      return updated;
    });
  }, []);

  // ── Submit ────────────────────────────────────────────────────────

  const handleSubmit = async (e) => {
    e.preventDefault();
    setServerError(null);

    // Validate
    const { rowErrors: errors, hasErrors } = validateStaffList(staffRows);
    setRowErrors(errors);

    if (hasErrors) return;

    setIsSubmitting(true);

    try {
      const result = await bulkCreateStaff(staffRows);

      if (result.success) {
        const created = result.data?.created || [];
        if (created.length > 0) {
          setCreatedCredentials(created);
        } else {
          setShowSuccess(true);
        }
      } else {
        setServerError(result.error);
      }
    } catch {
      setServerError('An unexpected error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Skip handler ──────────────────────────────────────────────────

  const handleSkip = () => {
    navigate(ROUTES.ORG);
  };

  // ── Render ────────────────────────────────────────────────────────

  return (
    <div
      className="staff-onboarding-page"
      style={{ backgroundColor: theme.background.primary, color: theme.text.primary }}
    >
      <div className="staff-onboarding-container">
        {/* Header */}
        <div className="staff-onboarding-header">
          <h1 style={{ color: theme.text.primary }}>Staff Onboarding</h1>
          <p style={{ color: theme.text.secondary }}>
            Add your clinic staff members. You can always manage staff later from your profile.
          </p>
        </div>

        {/* Server Error */}
        {serverError && (
          <div
            className="staff-server-error"
            style={{
              backgroundColor: theme.brand.errorBg,
              borderColor: theme.brand.error,
              color: theme.brand.error,
            }}
          >
            <span className="staff-error-icon">⚠️</span>
            <p>{serverError}</p>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} noValidate>
          {staffRows.map((row, idx) => (
            <StaffFormRow
              key={row._key}
              data={row}
              errors={rowErrors[idx] || {}}
              index={idx}
              onChange={handleFieldChange}
              onRemove={removeRow}
              disabled={isSubmitting}
              showRemove={staffRows.length > 1}
              departments={departments}
            />
          ))}

          {/* Add Row */}
          <button
            type="button"
            className="staff-add-btn"
            onClick={addRow}
            disabled={isSubmitting}
            style={{
              color: theme.brand.purple,
              borderColor: theme.brand.purple,
            }}
          >
            <span className="staff-add-icon">+</span>
            Add Staff Member
          </button>

          {/* Actions */}
          <div className="staff-onboarding-actions">
            <button
              type="button"
              className="staff-skip-btn"
              onClick={handleSkip}
              disabled={isSubmitting}
              style={{ color: theme.text.secondary }}
            >
              Skip for now
            </button>
            <button
              type="submit"
              className="staff-submit-btn"
              disabled={isSubmitting}
              style={{
                background: theme.gradient.userMessage,
                color: '#fff',
              }}
            >
              {isSubmitting ? (
                <>
                  <span className="staff-spinner" />
                  Adding Staff...
                </>
              ) : (
                'Add Staff'
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Success modal (fallback if no credentials returned) */}
      <ConfirmationModal
        isOpen={showSuccess}
        title="Staff Added Successfully"
        message="Your staff members have been added. You can manage them anytime from your profile."
        ctaLabel="Go to Chat"
        icon="🎉"
        onConfirm={() => {
          setShowSuccess(false);
          navigate(ROUTES.ORG);
        }}
      />

      {/* Credentials modal — shows usernames + temp passwords */}
      <StaffCredentialsModal
        isOpen={createdCredentials.length > 0}
        credentials={createdCredentials}
        onClose={() => {
          setCreatedCredentials([]);
          navigate(ROUTES.ORG);
        }}
      />
    </div>
  );
}

export default StaffOnboardingPage;
